import { type Dictionary, groupBy, uniq } from 'lodash';

import {
  doesToolSupportsSubTemplates,
  GENERATION_TOOL,
  getToolCopy,
  getToolTitle,
} from 'src/data/generationTool';
import { isDocumentProjectType } from 'src/data/project';

import { PROMPT_SUB_TEMPLATE_ID } from './const';
import type { TemplateTag } from './tags';
import type {
  CategorizedCustomTemplate,
  GenericOrSubTemplate,
  GenericTemplate,
  SubTemplate,
} from './types';

export const mergeGenericAndSubTemplates = (
  genericTemplates: GenericTemplate[],
  subTemplates: SubTemplate[],
  customTemplates: CategorizedCustomTemplate[] = []
) => {
  const groupedSubTemplates = groupBy(subTemplates, 'generationTool');
  const groupedCustomTemplates = groupBy(customTemplates, 'categoryGenerationTool');
  return genericTemplates.flatMap((template) =>
    mergeSubTemplates(template, groupedSubTemplates, groupedCustomTemplates)
  );
};

export const filterTemplatesByTag = (
  templates: GenericOrSubTemplate[],
  tag: TemplateTag
): GenericOrSubTemplate[] => {
  return templates.filter((template) => Boolean(template.tags?.includes(tag)));
};

const mergeSubTemplates = (
  template: GenericTemplate,
  groupedSubTemplates: Dictionary<SubTemplate[]>,
  groupedCustomTemplates: Dictionary<CategorizedCustomTemplate[]>
): GenericOrSubTemplate[] => {
  // A sub-template is listed beside the tool it belongs to, so it goes wherever that tool goes.
  // Most carry no tags of their own — inheriting is what puts them in the same tabs — and the few
  // that do keep theirs, which lists them in an audience tab the tool itself is not in.
  const toolSubTemplates = (groupedSubTemplates[template.generationTool] || []).map(
    (subTemplate) => ({
      ...subTemplate,
      tags: uniq([...(template.tags || []), ...(subTemplate.tags || [])]),
    })
  );
  // A card listed under a tool starts that tool's kind of project, and a customer template now
  // only ever starts a document one — so it belongs under a document tool and nowhere else.
  const toolCustomTemplates = (
    isDocumentProjectType(template.projectType)
      ? groupedCustomTemplates[template.generationTool] || []
      : []
  ).map((customTemplate) => ({
    ...customTemplate,
    generationTool: GENERATION_TOOL.customMultiPrompt,
    // A customer template carries no tags of its own, so inheriting these is what puts it in
    // exactly the tabs its generation tool appears in
    tags: template.tags,
  }));

  if (toolSubTemplates.length === 0 && toolCustomTemplates.length === 0) {
    return [template];
  }

  return [...toolCustomTemplates, buildPromptTemplate(template), ...toolSubTemplates];
};

const buildPromptTemplate = (template: GenericTemplate): GenericOrSubTemplate => {
  // A tool listed with custom templates alone has no sub-template screen to fall back to, so it
  // keeps its plain card instead of gaining a "prompt" entry.
  if (!doesToolSupportsSubTemplates(template.generationTool)) {
    return template;
  }

  return {
    ...template,
    subTemplateData: {
      id: PROMPT_SUB_TEMPLATE_ID,
      tool: template.generationTool,
      name: getToolTitle(template.generationTool) ?? '',
      description: getToolCopy(template.generationTool) ?? '',
    },
  };
};
